import {
  useMemo,
  useState,
} from 'react'

import { ModelPicker } from './components/ModelPicker'
import {
  PreferenceImportPage,
  PreferenceSharePage,
} from './components/PreferenceTransferPage'
import type { PreferenceImportCandidate } from './components/preferenceTransfer'
import { ProviderIcon } from './components/ProviderIcon'
import { SemanticStatus } from './components/SemanticStatus'
import { ROUTE_CARDS } from './data/routeCards'
import type {
  Assessment,
  ModelTier,
  WorkItem,
} from './domain/types'
import { useSemanticAnalysis } from './embedding/useSemanticAnalysis'
import { conservativePreviewOutcomeLenses } from './engine/conservativePreview'
import type { InferredWorkItemContext } from './engine/inferWorkItemContext'
import {
  assessOutcomeLenses,
  type OutcomeLensId,
  type OutcomeLensResult,
} from './engine/outcomeLenses'
import type { SemanticConfirmationResult } from './engine/semanticConfirmation'
import {
  applySemanticOutcomePolicy,
  type SemanticOutcomePolicyResult,
} from './engine/semanticOutcome'

const TIER_COPY: Record<
  ModelTier,
  {
    eyebrow: string
    emoji: string
    title: string
    summary: string
  }
> = {
  direct: {
    eyebrow: 'Direct route',
    emoji: '⚡',
    title: 'Fast, instruction-following model',
    summary:
      'The task is specific enough to start with an efficient model.',
  },
  standard: {
    eyebrow: 'Standard route',
    emoji: '🧭',
    title: 'Balanced general model',
    summary:
      'Several steps or judgment calls make a balanced model the safer start.',
  },
  deep: {
    eyebrow: 'Deep route',
    emoji: '🔬',
    title: 'Reasoning-capable model',
    summary:
      'Ambiguity, breadth, root-cause work, or risk calls for a stronger model.',
  },
}

const OUTCOME_LENS_OPTIONS: Array<{
  id: OutcomeLensId
  label: string
  copy: string
}> = [
  {
    id: 'fastest',
    label: 'Fastest',
    copy: 'Use the fastest route that still meets the task’s requirements.',
  },
  {
    id: 'best-fit',
    label: 'Best fit',
    copy: 'Balance capability, effort, and the work inferred from your prompt.',
  },
  {
    id: 'safest',
    label: 'Safest',
    copy: 'Prefer extra capability for a more cautious starting point.',
  },
]

const OUTCOME_LENS_KEYS = {
  fastest: 'fastest',
  'best-fit': 'bestFit',
  safest: 'safest',
} as const satisfies Record<
  OutcomeLensId,
  'fastest' | 'bestFit' | 'safest'
>

function signalBand(
  value: number,
): 'Lower' | 'Moderate' | 'Higher' {
  if (value >= 60) return 'Higher'
  if (value >= 32) return 'Moderate'
  return 'Lower'
}

const EXECUTION_MODE_LABELS: Record<
  WorkItem['executionMode'],
  string
> = {
  chat: 'Chat',
  'code-edit': 'Code edit',
  agent: 'Agent',
  research: 'Research',
}

const STAKES_LABELS: Record<WorkItem['stakes'], string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
}

const QUALITY_LABELS: Record<
  WorkItem['targetQuality'],
  string
> = {
  draft: 'Draft',
  usable: 'Usable',
  production: 'Production',
}

function OutcomeLensControl({
  value,
  onChange,
}: {
  value: OutcomeLensId
  onChange: (value: OutcomeLensId) => void
}) {
  const [motionInput, setMotionInput] = useState<
    'keyboard' | 'pointer'
  >('keyboard')
  const selected =
    OUTCOME_LENS_OPTIONS.find((option) => option.id === value) ??
    OUTCOME_LENS_OPTIONS[1]!

  return (
    <fieldset className="outcome-lens">
      <legend>Optimize for</legend>
      <div
        className="outcome-lens__options"
        data-selected={value}
        data-motion-input={motionInput}
      >
        <span
          className="outcome-lens__indicator"
          aria-hidden="true"
        />
        {OUTCOME_LENS_OPTIONS.map((option) => (
          <label
            key={option.id}
            onPointerDown={() => setMotionInput('pointer')}
          >
            <input
              type="radio"
              name="outcome-lens"
              value={option.id}
              checked={value === option.id}
              onKeyDown={() => setMotionInput('keyboard')}
              onChange={() => onChange(option.id)}
            />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      <p className="outcome-lens__copy">{selected.copy}</p>
    </fieldset>
  )
}

function InferredContext({
  context,
}: {
  context: InferredWorkItemContext
}) {
  const facets = [
    {
      label: 'Mode',
      value: EXECUTION_MODE_LABELS[context.executionMode.value],
      source: context.executionMode.source,
    },
    {
      label: 'Stakes',
      value: STAKES_LABELS[context.stakes.value],
      source: context.stakes.source,
    },
    {
      label: 'Finish',
      value: QUALITY_LABELS[context.targetQuality.value],
      source: context.targetQuality.source,
    },
  ]

  return (
    <section
      className="inferred-context"
      aria-labelledby="inferred-context-title"
    >
      <h3 id="inferred-context-title">Inferred context</h3>
      <dl className="inferred-facets">
        {facets.map((facet) => (
          <div key={facet.label}>
            <dt>{facet.label}</dt>
            <dd>
              <strong>{facet.value}</strong>
              <span>
                {facet.source === 'explicit-prompt-signal'
                  ? 'Prompt signal'
                  : 'Default'}
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function SiteHeader({ about = false }: { about?: boolean }) {
  return (
    <header className="site-header">
      <a className="wordmark" href="/" aria-label="SamRouter home">
        SamRouter
      </a>
      <nav aria-label="Primary">
        {about ? (
          <a href="/">Back to tool</a>
        ) : (
          <>
            <span className="local-status">
              <span aria-hidden="true" />
              Prompt stays local
            </span>
            <a href="/about">About</a>
          </>
        )}
      </nav>
    </header>
  )
}

function Result({
  assessment,
  inferredContext,
  outcomeLens,
  onOutcomeLensChange,
  conservativePreview,
  semanticConfirmation,
}: {
  assessment: Assessment
  inferredContext: InferredWorkItemContext
  outcomeLens: OutcomeLensId
  onOutcomeLensChange: (value: OutcomeLensId) => void
  conservativePreview: boolean
  semanticConfirmation: SemanticConfirmationResult | null
}) {
  const copy = TIER_COPY[assessment.tier]
  const routeStateLabel =
    outcomeLens === 'fastest'
      ? 'Fastest lens'
      : outcomeLens === 'safest'
        ? 'Safest lens'
        : conservativePreview
          ? 'Conservative preview'
          : semanticConfirmation?.status === 'confirmed'
            ? 'Locally confirmed'
            : semanticConfirmation?.status === 'abstained' ||
                semanticConfirmation?.status === 'disagreement'
              ? 'Conservative result'
              : 'Locally checked'
  const matchedCards = assessment.semanticMatches
    .map((match) => ({
      match,
      card: ROUTE_CARDS.find(
        (card) => card.id === match.routeCardId,
      ),
    }))
    .filter(
      (
        entry,
      ): entry is {
        match: (typeof assessment.semanticMatches)[number]
        card: (typeof ROUTE_CARDS)[number]
      } => Boolean(entry.card),
    )
  const policySources = ROUTE_CARDS.filter(
    (card) =>
      card.mapping.tier === assessment.tier &&
      card.temporalScope === 'current',
  ).slice(0, 3)
  const sources =
    matchedCards.length > 0
      ? matchedCards.slice(0, 3).map(({ card, match }) => ({
          card,
          similarity: match.similarity,
        }))
      : policySources.map((card) => ({
          card,
          similarity: undefined,
        }))
  const visibleReasons = [
    ...assessment.reasons.filter(({ id }) =>
      id.startsWith('semantic-confirmation-'),
    ),
    ...assessment.reasons.filter(({ id }) =>
      id.startsWith('outcome-lens-'),
    ),
    ...assessment.reasons.filter(
      ({ id }) =>
        !id.startsWith('semantic-confirmation-') &&
        !id.startsWith('outcome-lens-'),
    ),
  ].slice(0, 3)

  return (
    <section
      className={`route-result route-result--${assessment.tier}`}
      id="analysis-result"
      aria-label="Analysis result"
    >
      <OutcomeLensControl
        value={outcomeLens}
        onChange={onOutcomeLensChange}
      />

      <div className="route-result__primary">
        <p className="route-label">
          <span className="route-emoji" aria-hidden="true">
            {copy.emoji}
          </span>
          <span>
            {routeStateLabel} · {copy.eyebrow}
          </span>{' '}
          · {assessment.effort} reasoning
        </p>
        <h2>{copy.title}</h2>
        <p>{copy.summary}</p>
      </div>

      <div className="result-actions">
        <ModelPicker
          recommendations={assessment.providerRecommendations}
          routeTier={assessment.tier}
        />

        <p className="result-action improvement-line">
          <span className="result-action__icon" aria-hidden="true">
            ✎
          </span>
          <span>
            <strong>Improve</strong>{' '}
            {assessment.improvements[0] ??
              'The prompt already has a concrete outcome and checkable finish line.'}
          </span>
        </p>
      </div>

      <details className="route-details">
        <summary>Why this route</summary>
        <div className="route-details__content">
          <InferredContext context={inferredContext} />

          <div className="signal-row" aria-label="Prompt signal indexes">
            <div aria-label={`Task demand: ${signalBand(assessment.taskDemand)}`}>
              <span>Demand</span>
              <strong>{signalBand(assessment.taskDemand)}</strong>
            </div>
            <div
              aria-label={`Prompt readiness: ${signalBand(
                assessment.promptReadiness,
              )}`}
            >
              <span>Readiness</span>
              <strong>
                {signalBand(assessment.promptReadiness)}
              </strong>
            </div>
            <div
              aria-label={`Execution risk: ${signalBand(
                assessment.executionRisk,
              )}`}
            >
              <span>Risk</span>
              <strong>{signalBand(assessment.executionRisk)}</strong>
            </div>
          </div>

          <ul className="reason-list">
            {visibleReasons.map((reason) => (
              <li key={reason.id}>
                <strong>{reason.label}</strong>
                <span>{reason.detail}</span>
              </li>
            ))}
          </ul>
          <p className="fine-print">
            These are qualitative heuristic bands, not benchmark
            scores or pass probabilities.
          </p>

          <section className="source-section">
            <h3>
              {matchedCards.length > 0
                ? 'Nearest sourced patterns'
                : 'Sources for this class'}
            </h3>
            <div className="source-list">
              {sources.map(({ card, similarity }) => (
                <a
                  href={card.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  key={card.id}
                >
                  <span className="source-list__identity">
                    <ProviderIcon
                      provider={card.provider}
                      modelName={card.title}
                      className="provider-icon--source"
                    />
                    <span>
                      {card.provider} · {card.title}
                    </span>
                  </span>
                  <small>
                    {typeof similarity === 'number'
                      ? `Similarity ${similarity.toFixed(3)}`
                      : card.authority.replace('-', ' ')}
                  </small>
                </a>
              ))}
            </div>
          </section>

        </div>
      </details>
    </section>
  )
}

function AboutPage() {
  return (
    <div className="app-shell app-shell--about">
      <SiteHeader about />
      <main className="about-page">
        <p className="about-kicker">About SamRouter</p>
        <h1>
          Find the right model class before you spend the tokens.
        </h1>
        <p className="about-lede">
          SamRouter suggests a starting route: direct, standard, or
          deep. Your prompt stays in the browser.
        </p>

        <div className="about-sections">
          <section>
            <h2>What it does</h2>
            <p>
              It separates task demand, prompt readiness, and
              execution risk. Best fit is automatic. Fastest favors
              speed; Safest favors capability. The result is a
              planning aid, not a promise that a particular model
              will pass your task.
            </p>
          </section>
          <section>
            <h2>How it works</h2>
            <p>
              A conservative route appears immediately. In the
              background,{' '}
              <span className="inline-model-name">
                <ProviderIcon
                  provider="Snowflake"
                  modelName="Snowflake Arctic Embed XS"
                />
                Snowflake Arctic Embed XS
              </span>{' '}
              prepares in this browser and compares the whole
              prompt plus its bounded pieces with sourced guidance.
              It can confirm Direct only when the deterministic
              check agrees; otherwise the safer floor stays in
              place.
            </p>
          </section>
          <section>
            <h2>What stays private</h2>
            <p>
              Prompt text is not sent to a server, saved, or added
              to analytics. When online and Data Saver is off,
              fixed-version embedding files download from
              SamRouter&apos;s model store and may be cached by
              your browser.
            </p>
          </section>
        </div>
        <p className="brand-note">
          Provider names and marks identify the referenced models
          and belong to their respective owners; no affiliation or
          endorsement is implied. Icons come from{' '}
          <a
            href="https://icons.lobehub.com/"
            target="_blank"
            rel="noreferrer"
          >
            Lobe Icons
          </a>{' '}
          under its{' '}
          <a href="/licenses/lobe-icons.txt">MIT license</a>.
        </p>
      </main>
    </div>
  )
}

function PreferenceRoute({
  mode,
  importCandidate,
}: {
  mode: 'share' | 'import'
  importCandidate: PreferenceImportCandidate
}) {
  return (
    <div className="app-shell app-shell--about">
      <SiteHeader about />
      {mode === 'share' ? (
        <PreferenceSharePage />
      ) : (
        <PreferenceImportPage candidate={importCandidate} />
      )}
    </div>
  )
}

function ToolPage() {
  const [prompt, setPrompt] = useState('')
  const [outcomeLens, setOutcomeLens] =
    useState<OutcomeLensId>('best-fit')
  const semantic = useSemanticAnalysis(prompt)
  const hasPrompt = prompt.trim().length > 0
  const currentSemanticDecision =
    semantic.analysis.phase === 'complete' &&
    semantic.analysis.prompt === prompt
      ? semantic.routeDecision
      : null
  const semanticComplete = currentSemanticDecision !== null

  const baseOutcomeLenses = useMemo(
    () => (hasPrompt ? assessOutcomeLenses(prompt) : null),
    [hasPrompt, prompt],
  )

  const semanticOutcome =
    useMemo<SemanticOutcomePolicyResult | null>(() => {
      if (
        !baseOutcomeLenses ||
        !semanticComplete ||
        !currentSemanticDecision
      ) {
        return null
      }

      return applySemanticOutcomePolicy({
        prompt,
        baseOutcomeLenses,
        semanticDecision: currentSemanticDecision,
        semanticMatches: semantic.matches,
      })
    }, [
      baseOutcomeLenses,
      currentSemanticDecision,
      prompt,
      semantic.matches,
      semanticComplete,
    ])
  const semanticConfirmation =
    semanticOutcome?.confirmation ?? null

  const outcomeLenses = useMemo<OutcomeLensResult | null>(() => {
    if (!baseOutcomeLenses) return null
    if (!semanticOutcome) {
      return conservativePreviewOutcomeLenses(
        prompt,
        baseOutcomeLenses,
      )
    }
    return semanticOutcome.outcomeLenses
  }, [
    baseOutcomeLenses,
    prompt,
    semanticOutcome,
  ])

  const selectedOutcome = outcomeLenses
    ? outcomeLenses[OUTCOME_LENS_KEYS[outcomeLens]]
    : null
  const assessment = selectedOutcome?.assessment ?? null

  return (
    <div className="app-shell">
      <a className="skip-link" href="#prompt-input">
        Skip to prompt
      </a>
      <SiteHeader />
      <main className="workspace">
        <h1 className="sr-only">SamRouter prompt analysis</h1>
        <section className="prompt-surface" aria-label="Prompt input">
          <label className="sr-only" htmlFor="prompt-input">
            Prompt to analyze
          </label>
          <textarea
            id="prompt-input"
            value={prompt}
            spellCheck
            placeholder="Paste a prompt…"
            onChange={(event) => setPrompt(event.target.value)}
          />
        </section>

        <SemanticStatus
          controller={semantic}
          hasPrompt={hasPrompt}
          currentPrompt={prompt}
          currentRoute={assessment?.tier}
        />

        {assessment && outcomeLenses && selectedOutcome ? (
          <Result
            assessment={assessment}
            inferredContext={outcomeLenses.inferredContext}
            outcomeLens={outcomeLens}
            onOutcomeLensChange={setOutcomeLens}
            conservativePreview={!semanticComplete}
            semanticConfirmation={semanticConfirmation}
          />
        ) : (
          <p className="empty-hint">
            Analysis appears here as you type.
          </p>
        )}
      </main>
    </div>
  )
}

export default function App({
  preferenceImport = { status: 'missing' },
}: {
  preferenceImport?: PreferenceImportCandidate
}) {
  const path =
    typeof window === 'undefined'
      ? '/'
      : window.location.pathname.replace(/\/+$/, '') || '/'

  if (path === '/about') return <AboutPage />
  if (path === '/preferences/share') {
    return (
      <PreferenceRoute
        mode="share"
        importCandidate={preferenceImport}
      />
    )
  }
  if (path === '/preferences/import') {
    return (
      <PreferenceRoute
        mode="import"
        importCandidate={preferenceImport}
      />
    )
  }
  return <ToolPage />
}
